/**
 * Longest edge sent to Graph, in pixels.
 *
 * Graph refuses a larger photo outright — `InvalidImageDimensions`, measured on
 * a real batch at 4595x3676 through 5000x4000, all of them barely over 1 MB, so
 * the refusal is about pixels and not about bytes. Microsoft documents no
 * threshold; 1920 is the value that has been shipping since the upload path
 * started capping, and it is 3x the largest rendition Graph serves back
 * (648x648), so nothing downstream can tell the difference.
 */
export const MAX_PHOTO_EDGE_PX = 1920;

/**
 * The box a source fits into, or `null` when it already fits and must be left
 * alone. Pure, so the arithmetic is testable without a canvas.
 */
export function fitWithin(
  width: number,
  height: number,
  cap = MAX_PHOTO_EDGE_PX,
): { width: number; height: number } | null {
  const longest = Math.max(width, height);
  if (longest <= cap) {
    return null;
  }
  const ratio = cap / longest;
  // Round rather than floor: a 4595x3676 lands on 1920x1536, and flooring the
  // short edge would silently change the aspect ratio by a pixel.
  return {
    width: Math.max(1, Math.round(width * ratio)),
    height: Math.max(1, Math.round(height * ratio)),
  };
}

/**
 * Shrinks a photo to {@link MAX_PHOTO_EDGE_PX} if it is over, and returns the
 * **same blob untouched** if it is not — the common case. Only what Graph would
 * refuse is re-encoded.
 *
 * The bitmap is decoded straight to the target size rather than drawn full-size
 * and scaled down: iOS Safari caps canvas *area* at roughly 16.7 Mpx, and a
 * 5000x4000 source is 20 Mpx, so a full-size canvas comes back blank with no
 * error at all. `imageOrientation: 'from-image'` applies the EXIF rotation
 * before it is dropped, without which every portrait phone photo would arrive
 * on its side.
 *
 * Fails open: if the browser cannot do any of this, the original bytes go up
 * and Graph's own refusal is displayed. A resize that cannot happen must not
 * become an upload that cannot happen.
 */
export async function downscaleToFit(
  blob: Blob,
  cap = MAX_PHOTO_EDGE_PX,
): Promise<Blob> {
  try {
    // `imageOrientation` est passé aux **deux** appels, et c'est le point : la
    // mesure et le redimensionnement doivent vivre dans le même repère. Un
    // moteur dont le défaut vaut encore `'none'` mesurerait les pixels stockés
    // pendant que le resize, lui, oriente — une photo en EXIF 6 serait forcée
    // dans une cible calculée pour l'autre sens, donc écrasée. Chrome 152
    // applique l'orientation quoi qu'on demande (la spec a retiré `'none'`),
    // mais s'appuyer là-dessus, c'est dépendre d'un défaut non écrit.
    const probe = await createImageBitmap(blob, {
      imageOrientation: 'from-image',
    });
    const target = fitWithin(probe.width, probe.height, cap);
    probe.close();
    if (!target) {
      return blob;
    }

    const bitmap = await createImageBitmap(blob, {
      resizeWidth: target.width,
      resizeHeight: target.height,
      resizeQuality: 'high',
      imageOrientation: 'from-image',
    });
    const canvas = document.createElement('canvas');
    canvas.width = bitmap.width;
    canvas.height = bitmap.height;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      return blob;
    }
    // A PNG's transparent pixels would otherwise come out black in a JPEG.
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(bitmap, 0, 0);
    bitmap.close();

    const resized = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob(resolve, 'image/jpeg', 0.92);
    });
    return resized ?? blob;
  } catch {
    return blob;
  }
}
